"use client";

import Link from "next/link";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@workspace/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@workspace/ui/components/card";
import { InputField } from "@workspace/ui/components/input";
import { Loader2, X } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";

import { signUp } from "@/lib/auth-client";
import { useSlyderzForm } from "app/hooks/useSlyderzForm";

export const signupFormSchema = z
	.object({
		firstName: z.string().min(1, { message: "First name is required" }).trim(),
		lastName: z.string().optional(),
		email: z.string().email().trim(),
		password: z
			.string()
			.min(1, { message: "Password must be 6 characters or longer" })
			.max(250)
			.trim(),
		passwordConfirmation: z
			.string()
			.min(1, { message: "Password must be 6 characters or longer" })
			.max(250)
			.trim(),
	})
	.refine((data) => data.password === data.passwordConfirmation, {
		message: "Passwords don't match",
		path: ["passwordConfirmation"], // set the path of the error
	});
export default function SignupForm() {
	const [image, setImage] = useState<File | null>(null);
	const [imagePreview, setImagePreview] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const router = useRouter();

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			setImage(file);
			const reader = new FileReader();
			reader.onloadend = () => {
				setImagePreview(reader.result as string);
			};
			reader.readAsDataURL(file);
		}
	};

	const handleForm = async (input: z.infer<typeof signupFormSchema>) => {
		await signUp.email(
			{
				email: input.email.toLowerCase(),
				password: input.password,
				name: input.firstName,
				lastName: input.lastName,
				image: undefined,
				callbackURL: "/",
			},
			{
				onResponse: () => {
					setLoading(false);
				},
				onRequest: () => {
					setLoading(true);
				},
				onSuccess: () => {
					toast.success("Account created successfully");
					router.push("/onboarding");
				},
				onError: (ctx: any) => {
					toast.error(ctx.error.message);
				},
			},
		);
	};

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useSlyderzForm(signupFormSchema, {
		firstName: "",
		lastName: "",
		email: "",
		password: "",
		passwordConfirmation: "",
	});

	return (
		<form
			id="signup-form"
			onSubmit={handleSubmit(handleForm)}
			className="space-y-6 mt-8 p-2"
		>
			<Card className="z-50 rounded-md rounded-t-none max-w-md">
				<CardHeader>
					<CardTitle className="text-lg md:text-xl">Sign Up</CardTitle>
					<CardDescription className="text-xs md:text-sm">
						Enter your information to create an account
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="grid gap-4">
						<div className="grid grid-cols-2 gap-4">
							<div className="grid gap-2">
								<InputField
									{...register("firstName")}
									label="First Name"
									placeholder="John"
									autoComplete="given-name"
									error={errors?.firstName?.message}
								/>
							</div>
							<div className="grid gap-2">
								<InputField
									{...register("lastName")}
									label="Last Name"
									placeholder="Smith"
									autoComplete="family-name"
									error={errors?.lastName?.message}
								/>
							</div>
						</div>
						<div className="grid gap-2">
							<InputField
								{...register("email")}
								label="Email"
								placeholder="johnsmith@test.com"
								autoComplete="email"
								error={errors?.email?.message}
							/>
						</div>
						<div className="grid gap-2">
							<InputField
								{...register("password")}
								label="Password"
								placeholder="Enter your password"
								autoComplete="new-password"
								type="password"
								error={errors?.password?.message}
							/>
						</div>
						<div className="grid gap-2">
							<InputField
								{...register("passwordConfirmation")}
								label="Password"
								placeholder="Enter your password"
								autoComplete="new-password"
								type="password"
								error={errors?.passwordConfirmation?.message}
							/>
						</div>
						{/* <div className="grid gap-2">
							<Label htmlFor="image">Profile Image (optional)</Label>
							<div className="flex items-end gap-4">
								{imagePreview && (
									<div className="relative w-16 h-16 rounded-sm overflow-hidden">
										<Image
											src={imagePreview}
											alt="Profile preview"
											layout="fill"
											objectFit="cover"
										/>
									</div>
								)}
								<div className="flex items-center gap-2 w-full">
									<Input
										id="image"
										type="file"
										accept="image/*"
										onChange={handleImageChange}
										className="w-full"
									/>
									{imagePreview && (
										<X
											className="cursor-pointer"
											onClick={() => {
												setImage(null);
												setImagePreview(null);
											}}
										/>
									)}
								</div>
							</div>
						</div> */}
						<Button type="submit" className="w-full" disabled={loading}>
							{loading ? (
								<Loader2 size={16} className="animate-spin" />
							) : (
								"Create an account"
							)}
						</Button>

						<div className="flex items-center">
							<p className="ml-4 text-center text-sm text-gray-500">
								Already have an account?{" "}
								<Link
									href="/login"
									className="font-semibold leading-6 text-indigo-600 hover:text-indigo-500"
								>
									Sign in
								</Link>
							</p>
						</div>
					</div>
				</CardContent>
			</Card>
		</form>
	);
}

async function convertImageToBase64(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => resolve(reader.result as string);
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
}
